import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import usePageTitle from '@hooks/usePageTitle/usePageTitle';
import { usePageContext } from '@providers/PageMetaProvider/model/PageContext';
import MainCard from '@components/ui/cards/MainCard';
import SettingsTemplates from '@features/settings-templates/containers/SettingsTemplates';
import { useLetterTemplateTypes } from '@features/settings-templates/hooks';
import { getLetterTemplateTypes } from '@api/letter-templates';

const pageValues = {
  title: 'Templates',
  description: "Your firm's Word templates, one per letter type",
};

/** Settings > Templates (ZLAYT-1679). Off on the server: back to Settings. */
function SettingsTemplatesPage() {
  usePageTitle('ZlaytAI - Templates');
  const { setValues } = usePageContext();
  const { data, mutate } = useLetterTemplateTypes();
  // A cached "off" from earlier in the session is asked again before the
  // user is sent back, so they are not bounced once the switch is on. A
  // fresh answer (the usual visit) needs no second request.
  const [startedOff] = useState(() => Boolean(data?.off));
  const [recheck, setRecheck] = useState<'pending' | 'off' | 'other'>(
    'pending'
  );
  useEffect(() => {
    if (!startedOff) return undefined;
    let alive = true;
    // Asked directly: on a failure SWR's mutate() hands back the stale
    // cached "off", which would bounce the user anyway.
    getLetterTemplateTypes()
      .then((answer) => {
        mutate(answer, { revalidate: false });
        if (alive) setRecheck(answer.off ? 'off' : 'other');
      })
      .catch(() => {
        // Stay on the page: revalidating records the error, and the list
        // shows it with Retry rather than bouncing on the stale answer.
        if (alive) setRecheck('other');
        mutate();
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (setValues) {
      setValues((prev) => ({
        ...prev,
        ...pageValues,
      }));
    }
  }, []);

  if (startedOff ? recheck === 'off' : data?.off) {
    return <Navigate replace to="/settings/personal-info" />;
  }
  if (startedOff && recheck === 'pending') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <MainCard>
      <SettingsTemplates />
    </MainCard>
  );
}

export default SettingsTemplatesPage;
